import { FC, memo, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useSnapshot } from 'valtio'
import { useTranslation } from 'react-i18next'

import { Button, Icon, IconColor, IconTypes, Modal, ScrollContainer, InitialsAvatar } from '@app/ui'
import { ButtonType, ModalType } from '@app/types'
import {
  AppMenuState,
  CurrentUserState,
  guestMode,
  resetCurrentUserState,
  showSignupPromptModal
} from '@app/storage'
import { AuthHelper } from '@app/helpers'
import { ROUTES } from '@app/constants'
import { useScrollLock } from '@app/helpers/hooks'
import { InstallAppContext } from '@app/context'
import ShareSocial from '../ShareSocial'
import { ErrorModal } from '../ErrorModal'
import RgBanner from '../RgBanner/RgBanner'
import LoginChooseLanguageScreen from '@app/screens/Login/LoginChooseLanguage'
import { InstallPromptAndroid } from '../UserEntry/components/InstallPrompt'
import { isInstalledApp } from '@app/helpers/userAgent'
import { promptInstall } from '@app/helpers/pwaTracking'
import { AppMenuProps } from './types'
import { MenuItemList } from './components/MenuItemList'
import ContactUsModal from '../ContactUsModal/ContactUsModal'
import { FaqTutorialsModal } from '../SupportBottomSheet/FaqTutorialsModal'
import TelegramBotSelection from './components/TelegramBotSelection'
import ChangeUserRoleView from './components/ChangeUserRole'
import HelpCenterScreen from './components/HelpCenterScreen'

type SupportRequestType = 'support' | 'bug' | 'feature'

const AppMenu: FC<AppMenuProps> = ({
  open,
  onClose,
  positionX,
}) => {
  const [ openSocial, setOpenSocial ] = useState(false)
  const [ loading, setLoading ] = useState(false)
  const [ shareLink, setShareLink ] = useState<string>(null)
  const [ shareLinkError, setShareLinkError ] = useState(false)
  const [ channelName, setChannelName ] = useState<string>('')
  const [ openLanguageSelect, setOpenLanguageSelect ] = useState(false)
  const [ openContactUs, setOpenContactUs ] = useState(false)
  const [ supportRequestType, setSupportRequestType ] = useState<SupportRequestType>('support')
  const [ openFaqTutorials, setOpenFaqTutorials ] = useState(false)
  const [ openSupportSheet, setOpenSupportSheet ] = useState(false)
  const [ telegramBotSelect, setTelegramBotSelect ] = useState(false)
  const [ becomeTipster, setBecomeTipster ] = useState(false)

  // Open directly to Help Center or Contact Us if triggered from outside (e.g. SSO error screen)
  const { initialView } = useSnapshot(AppMenuState)
  useEffect(() => {
    if (!open) return
    if (initialView === 'helpCenter') {
      setOpenSupportSheet(true)
      AppMenuState.initialView = undefined
    } else if (initialView === 'contactSupport') {
      setSupportRequestType('support')
      setOpenContactUs(true)
      AppMenuState.initialView = undefined
    }
  }, [ open, initialView ])

  const isGuest = guestMode()
  const {
    name,
    surname,
    avatarUrl,
    username,
    id,
    isTouring,
    tourStage,
    currentTourStage,
    userEmail,
  } = useSnapshot(CurrentUserState)

  const { event: installEvent } = useContext(InstallAppContext)
  const showInstallBanner = isInstalledApp() === 'web' && !!installEvent
  const { unlockScroll } = useScrollLock()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const disableSub = useMemo(() => {
    if(currentTourStage == 'userInviteMenu') return true
    if(tourStage.ContentDataTip || tourStage.ContentImageTip) return false
    if(isTouring) return true
    return isGuest
  }, [ isTouring, currentTourStage, tourStage ])


  const onProfileClick = () => {
    unlockScroll()
    // Remove inert attribute from root to ensure page is interactive after navigation
    const rootElement = document.getElementById('root')
    rootElement?.removeAttribute('inert')
    handleOnClose()
    navigate(`${ROUTES.profile.short}/${id}`)
  }


  const onCloseSocial = () => setOpenSocial(false)

  function handleOnClose() {
    setOpenLanguageSelect(false)
    setOpenContactUs(false)
    setOpenSupportSheet(false)
    onClose()
  }

  const displayName = name || surname ? `${name || ''} ${surname || ''}`.trim() : username


  const installApp = async () => {
    if (!installEvent) return

    try {
      await promptInstall(installEvent, 'app_menu')
    } catch (e) {
      console.error(e)
    }
  }

  const handleLegalClick = () => {
    handleOnClose()
    navigate(ROUTES.legal.short)
  }

  const handleContactSupport = (type: SupportRequestType) => {
    setSupportRequestType(type)
    setOpenContactUs(true)
  }

  return <>
    <MenuContainer
      $fullScreenPage={openContactUs || openSupportSheet}
      left={positionX}
      locked={true}
      open={open}
      type={ModalType.appMenu}
      onClose={handleOnClose}
      zIndex={1000}
    >
      {openContactUs ? (
        <ContactUsModal
          inline
          open={openContactUs}
          requestType={supportRequestType}
          onClose={() => setOpenContactUs(false)}
          onSuccessClose={handleOnClose}
        />
      ) : openSupportSheet ? (
        <HelpCenterScreen
          title={t('helpCenter.back')}
          onBack={() => setOpenSupportSheet(false)}
          onContactSupport={handleContactSupport}
          onLegalClick={handleLegalClick}
        />
      ) : (
        <>
          <StyledHeader className="vb-flex-row-spb-c" isTouring={isTouring}>
            <StyledClosedButton onClick={handleOnClose} className="vb-flex-row-c-c close-icon">
              <Icon type={IconTypes.close}
                color={isTouring ? IconColor.white : IconColor.textPrimary}
                width={24}
                height={24}
              />
            </StyledClosedButton>
          </StyledHeader>
          <StyledProfileRow onClick={isTouring || isGuest ? () => null : onProfileClick} $clickable={!isTouring && !isGuest}>
            <div className="vb-flex-row-fs-c">
              {!isGuest &&
                <StyledAvatar>
                  <InitialsAvatar
                    avatarUrl={avatarUrl}
                    displayName={displayName}
                    nameCircleStyle={{ width: 50, height: 50, fontSize: 18 }}
                  />
                </StyledAvatar>}
              <div className={isGuest? 'profile': 'profile-alt'}>
                <span className="profile-name">{isGuest ? t(displayName) : displayName}</span>
                {isGuest ? (
                  <StyledGuestPill>
                    <span>{t('incognito.limitedAccess')}</span>
                  </StyledGuestPill>
                ) : (
                  <span className="profile-email">{userEmail}</span>
                )}
              </div>
            </div>
            {!isGuest && (
              <Icon
                type={IconTypes.angleRight}
                color={IconColor.zinc400}
                width={16}
                height={16}
              />
            )}
          </StyledProfileRow>
          {isGuest && <GuestSignupButton
            onClick={() => {
              showSignupPromptModal({
                message: t('incognito.modal.loginDefaultMessage'),
                title: t('incognito.modal.loginTitle'),
              })()
              handleOnClose()
            }}
          >
            <Icon
              type={IconTypes.logoutLineIcon}
              color={IconColor.white}
              width={9}
              height={10}
            />
            {t('incognito.signup')}
          </GuestSignupButton>}
          <StyledScroll isTouring={isTouring}>
            <MenuItemList
              disableSub={!!disableSub}
              setOpenLanguageSelect={setOpenLanguageSelect}
              setChannelName={setChannelName}
              setShareLink={setShareLink}
              setLoading={setLoading}
              setOpenSocial={setOpenSocial}
              setShareLinkError={setShareLinkError}
              setOpenSupportSheet={setOpenSupportSheet}
              setTelegramBotSelect={setTelegramBotSelect}
              setBecomeTipster={setBecomeTipster}
            />
          </StyledScroll>
          {!isGuest && (
            <StyledLogoutSection>
              <StyledLogoutButton onClick={async () => {
                unlockScroll()
                const rootElement = document.getElementById('root')
                rootElement?.removeAttribute('inert')
                await AuthHelper.logOut()
                resetCurrentUserState()
                AppMenuState.opened = false
                navigate(ROUTES.root)
              }}>
                <Icon type={IconTypes.logout} color={IconColor.red} width={20} height={20} />
                <span>{t('menu.logout')}</span>
              </StyledLogoutButton>
            </StyledLogoutSection>
          )}
          <StyledBottomSection>
            {showInstallBanner && (
              <StyledInstallBanner className="vb-flex-row-fs-c">
                <InstallPromptAndroid installApp={installApp}/>
              </StyledInstallBanner>
            )}
            <RgBanner />
          </StyledBottomSection>
        </>
      )}
      {openLanguageSelect && <LoginChooseLanguageScreen onBack={() => setOpenLanguageSelect(false)} />}
      <ShareSocial
        open={openSocial}
        link={shareLink}
        loading={loading}
        onClose={onCloseSocial}
        channelName={channelName}
      />
      <ErrorModal
        open={shareLinkError}
        onClose={() => setShareLinkError(false)}
        title={t('common.attention')}
        description={t('errors.UNKNOWN')}
        bottomContent={
          <Button type={ButtonType.primary} fluid onClick={() => setShareLinkError(false)}>
            {t('common.gotIt')}
          </Button>
        }
      />
    </MenuContainer>
    <FaqTutorialsModal
      open={openFaqTutorials}
      onClose={() => setOpenFaqTutorials(false)}
    />
    <ChangeUserRoleView
      open={becomeTipster}
      onClose={() => {
        setBecomeTipster(false)
        onClose()
      }}
    />
    <TelegramBotSelection
      open={telegramBotSelect}
      onClose={() => {
        setTelegramBotSelect(false)
        onClose()
      }}
    />
  </>
}

const GuestSignupButton = styled(Button)`
  margin-top: 16px;
  border: 1px solid var(--violet-700);
  background: var(--violet-600);
`

const StyledClosedButton = styled.button`
  border: none;
  border-radius: 100%;
  background: transparent;
  margin-right: 30px;
`

const StyledGuestImageContainer = styled.div`
  border: none;
  background: #C9D2DE;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 11.5px 13px 12.5px 13px;
  border-radius: 100%;
  margin-right: 15px;
`

const StyledAvatar = styled.div`
  margin-right: 16px;

  .avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
  }
`

const StyledProfileRow = styled.div<{ $clickable: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  gap: 12px;

  > .vb-flex-row-fs-c {
    min-width: 0;
    flex: 1 1 auto;
  }

  > svg {
    flex-shrink: 0;
  }

  .profile {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .profile-alt {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: baseline;
    width: 100%;
  }
  .profile-name {
    cursor: default;
    font-size: 16px;
    line-height: 24px;
    font-weight: 600;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .profile-email {
    color: var(--text-nonary);
    font-size: 12px;
    font-weight: 400;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`
const StyledGuestPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border: 1px solid var(--violet-800);
  color: var(--violet-300);
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 10px;
  span {
    padding-right: 3px;
  }
`



const StyledScroll = styled(ScrollContainer)<{ isTouring: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  padding-top: 28px;
  .menu-toolTip .content {
    width: 100%;
  }

  .menu-toolTip .title {
    color: #2b2d30;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px; /* 133.333% */
    letter-spacing: -0.12px;
  }

  .menu-toolTip .msg {
    margin-top: 4px;
    color: #616c78;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
    letter-spacing: -0.12px;
  }

  .react-tooltip {
    width: 90%;
    position: fixed;
  }
  ${({ isTouring }) =>
    isTouring ? 'background: rgba(60, 58, 57, 0.60);backdrop-filter: blur(16px);' : ''}
`


const MenuContainer = styled(Modal)<{ left: number; $fullScreenPage: boolean }>`
  &.type-app-menu {
    background: rgba(0, 0, 0, 0.6);
    z-index: 1000 !important;
    align-items: flex-start;
    justify-content: flex-start;

    .modal-content.type-app-menu {
      background: var(--bg-secondary);
      box-sizing: border-box;
      padding: ${({ $fullScreenPage }) => ($fullScreenPage ? '0' : '10px')};
      padding-top: ${({ $fullScreenPage }) => ($fullScreenPage ? '0' : '25px')};
      width: ${({ $fullScreenPage }) => ($fullScreenPage ? '100%' : 'min(360px, calc(100% - 44px))')};
      height: 100%;
      min-height: 100dvh;
      border-radius: 0;
      overflow: hidden;
    }
  }
`

const StyledHeader = styled.div<{isTouring: boolean}>`
  ${({ isTouring }) => isTouring? 'background: rgba(60, 58, 57, 0.60);backdrop-filter: blur(16px);': ''}

  width: 100%;
  padding: 8px;
  display: flex;
  justify-content: flex-end;

  .close-icon {
    cursor: pointer;
  }
`
const StyledBottomSection = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
`

const StyledInstallBanner = styled.div`
  padding: 1rem;
  position: relative;
`


const StyledLogoutSection = styled.div`
  margin-top: 24px;
  padding-top: 40px;
  padding-bottom: 16px;
  border-top: 1px solid #4F4F4F;
`

const StyledLogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: transparent;
  border: none;
  padding: 0 20px;
  cursor: pointer;
  width: 100%;

  span {
    color: var(--color-danger);
    font-size: 14px;
    font-weight: 500;
  }
`

export default memo(AppMenu)
